import { oids } from "../../../configs/Settings";

function onlyDigits(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value).replace(/\D/g, "");
}

/**
 * Normaliza o payload "alt" do Validate (não-FHIR) para um Organization FHIR.
 *
 * Regra de compatibilidade:
 * - Mantém o mapeamento alinhado com o registro (`FormSubscription`), pois o backend pode
 *   retornar dois formatos diferentes e o frontend precisa tratar ambos.
 */
export function normalizeAltToFhirOrganization(params: {
  alt: unknown;
  matchedSocioName: string;
  creatorCpf: string;
}): fhir5.Organization {
  const { alt, matchedSocioName, creatorCpf } = params;

  const a = alt as {
    ni?: unknown;
    nomeEmpresarial?: unknown;
    nomeFantasia?: unknown;
    endereco?: {
      uf?: unknown;
      municipio?: { descricao?: unknown } | unknown;
      pais?: { descricao?: unknown } | unknown;
      logradouro?: unknown;
      numero?: unknown;
      complemento?: unknown;
      bairro?: unknown;
      cep?: unknown;
    } | null;
    telefones?: { ddd?: unknown; numero?: unknown } | unknown;
    correioEletronico?: unknown;
    naturezaJuridica?: { codigo?: unknown; descricao?: unknown } | unknown;
    cnaePrincipal?: { codigo?: unknown; descricao?: unknown } | unknown;
    cnaeSecundarias?: Array<{ codigo?: unknown; descricao?: unknown }> | unknown;
  };

  const cnpjDigits = onlyDigits(a?.ni);
  const cepDigits = onlyDigits(a?.endereco?.cep);
  const state = a?.endereco?.uf ? String(a.endereco.uf) : "";
  const city =
    a?.endereco?.municipio &&
    typeof a.endereco.municipio === "object" &&
    (a.endereco.municipio as { descricao?: unknown })?.descricao
      ? String((a.endereco.municipio as { descricao?: unknown }).descricao)
      : "";
  const country =
    a?.endereco?.pais &&
    typeof a.endereco.pais === "object" &&
    (a.endereco.pais as { descricao?: unknown })?.descricao
      ? String((a.endereco.pais as { descricao?: unknown }).descricao)
      : "BRASIL";

  const addressLine: string[] = [
    a?.endereco?.bairro ? String(a.endereco.bairro) : "",
    a?.endereco?.logradouro ? String(a.endereco.logradouro) : "",
    a?.endereco?.numero ? String(a.endereco.numero) : "",
    a?.endereco?.complemento ? String(a.endereco.complemento) : "",
  ].filter(Boolean);

  const phoneDigits =
    a?.telefones && typeof a.telefones === "object"
      ? `${onlyDigits((a.telefones as { ddd?: unknown }).ddd)}${onlyDigits(
          (a.telefones as { numero?: unknown }).numero,
        )}`
      : "";

  const naturezaJuridicaCoding = (() => {
    const nj =
      a?.naturezaJuridica && typeof a.naturezaJuridica === "object"
        ? (a.naturezaJuridica as { codigo?: unknown; descricao?: unknown })
        : undefined;
    if (!nj?.codigo && !nj?.descricao) return null;
    return {
      system: "http://example.org/naturezaJuridica",
      code: nj?.codigo ? String(nj.codigo) : "",
      display: nj?.descricao ? String(nj.descricao) : "",
    };
  })();

  const cnaePrincipalCoding = (() => {
    const cp =
      a?.cnaePrincipal && typeof a.cnaePrincipal === "object"
        ? (a.cnaePrincipal as { codigo?: unknown; descricao?: unknown })
        : undefined;
    if (!cp?.codigo && !cp?.descricao) return null;
    return {
      system: cp?.codigo ? String(cp.codigo) : "",
      display: cp?.descricao ? String(cp.descricao) : "",
      use: "official",
    };
  })();

  const cnaeSecundariasCoding = Array.isArray(a?.cnaeSecundarias)
    ? (a.cnaeSecundarias as Array<{ codigo?: unknown; descricao?: unknown }>)
        .map((c) => ({
          system: "cnaeSecundaria",
          value: c?.codigo ? String(c.codigo) : "",
          display: c?.descricao ? String(c.descricao) : "",
        }))
        .filter((c) => c.value || c.display)
    : [];

  const qualificationCoding = [
    ...(naturezaJuridicaCoding ? [naturezaJuridicaCoding] : []),
    ...(cnaePrincipalCoding ? [cnaePrincipalCoding] : []),
    ...cnaeSecundariasCoding,
  ];

  const organization = {
    resourceType: "Organization",
    id: "",
    name: a?.nomeEmpresarial ? String(a.nomeEmpresarial) : "",
    identifier: [
      {
        system: `urn:oid:${oids.cnpj}`,
        value: cnpjDigits,
      },
    ],
    contact: [
      {
        // Mantemos compatibilidade com o formato já esperado no registro (array em `address`)
        address: [
          {
            country,
            state,
            city,
            line: addressLine,
            postalCode: cepDigits,
          },
        ],
      },
      {
        telecom: [
          ...(phoneDigits ? [{ system: "phone", value: phoneDigits }] : []),
          ...(a?.correioEletronico
            ? [{ system: "email", value: String(a.correioEletronico) }]
            : []),
        ],
      },
    ],
    type: [
      {
        coding: [
          {
            system: "http://terminology.hl7.org/CodeSystem/organization-type",
            code: "prov",
            display: "Healthcare Provider",
          },
        ],
      },
    ],
    alias: [
      a?.nomeFantasia
        ? String(a.nomeFantasia)
        : a?.nomeEmpresarial
          ? String(a.nomeEmpresarial)
          : "",
    ].filter(Boolean),
    qualification: [
      {
        code: {
          coding: qualificationCoding,
        },
      },
    ],
    active: true,
    extension: [
      {
        url: "https://fluxmed.cloud/StructureDefinition/OrganizationManager",
        extension: [
          {
            url: "id",
            valueIdentifier: {
              system: "https://fluxmed.cloud/creator-id-system",
              value: creatorCpf,
            },
          },
          {
            url: "name",
            valueString: matchedSocioName,
          },
          {
            url: "purpose",
            valueString: "socio",
          },
        ],
      },
    ],
  };

  return organization as unknown as fhir5.Organization;
}

